import React, { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import TablePagination from "components/TablePagination";
import { Badge } from "components/ChecklistRow";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import {
  listarHomologacao,
  aprovarTransportadoraHomologacao,
  devolverTransportadoraHomologacao,
  reenviarTransportadoraHomologacao,
  completarReceitaHomologacao,
} from "services/onboardingTransportadoraAdminApi";
import TransportadoraDetalheModal from "views/Admin/Transportadoras/TransportadoraDetalheModal";
import { formatDocument } from "utils/formatDoc";
import { formatDateTime } from "utils/formatDate";

// Fila de homologação (PO1-3107), por TRANSPORTADORA desde a PO1-3281 (épico
// PO1-3279): uma linha por matriz, com os embarcadores que esperam. Critérios
// são da transportadora (dados principais + contato de TI); o canal de CT-e e
// o 1º CT-e saíram — nascem no onboarding, depois da aprovação. Desde a
// PO1-3498 o contato de TI é só aviso: aprovar exige apenas os dados principais. Aprovar libera
// todos os vínculos dela de uma vez; Devolver com motivo; Reenviar quando a
// Everlog mesma corrigiu o cadastro de uma devolvida.
// PO1-3491/PO1-3492: a Everlog corrige pela própria fila. O nome abre o
// cadastro; os selos de critério são clicáveis e abrem direto a aba certa
// (Dados ou Contatos); "Completar pela Receita" preenche o endereço vazio.
const STATUS_OPTIONS = [
  { value: "pendente", label: "Aguardando homologação" },
  { value: "devolvido", label: "Devolvidas" },
  { value: "homologado", label: "Homologadas" },
  { value: "todos", label: "Todas" },
];
const PAGE_SIZES = [10, 20, 50, 100];
const FASE_LABEL = {
  EM_CONFIGURACAO: "Aguardando homologação",
  AGUARDANDO_HOMOLOGACAO: "Aguardando homologação",
  EM_HOMOLOGACAO: "Pronta para homologar",
  DEVOLVIDO: "Devolvida",
  EM_ONBOARDING: "Em onboarding",
  OPERACIONAL: "Operacional",
  REMOVIDO: "Removida",
};
const STATUS_HOMOLOGADOS = ["HOMOLOGADO", "EM_ONBOARDING", "OPERACIONAL"];

const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const chipStyle = {
  display: "inline-block",
  padding: "0.1rem 0.55rem",
  borderRadius: 999,
  fontSize: "0.74rem",
  background: AUTOPILOT_COLORS.insetSurface,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  color: AUTOPILOT_COLORS.text,
  marginRight: 4,
  marginBottom: 4,
  whiteSpace: "nowrap",
};

// A API devolve { items, count } com `transportadora` aninhada; o contrato da
// PO1-3281 também prevê { itens, total } com cnpj/fantasia no topo. Normaliza.
export function normalizarLinha(row) {
  const t = row.transportadora || {};
  const embarcadores = (row.embarcadores || []).map((e) => ({
    id: e.id !== undefined ? e.id : e.idEmbarcador,
    nome: e.nome || e.embarcador || "",
    idHabilitacao: e.idHabilitacao,
    status: e.status,
    criadoEm: e.criadoEm || e.creationTime || null,
  }));
  return {
    key: row.idTransportadora || row.id,
    idTransportadora: row.idTransportadora || (t.id !== undefined ? t.id : null),
    fantasia: row.fantasia || t.fantasia || "",
    razaoSocial: row.razaoSocial || t.razaoSocial || "",
    cnpj: row.cnpj || t.cnpj || "",
    status: row.status,
    fase: row.fase,
    desde: row.desde || row.creationTime || null,
    motivoDevolucao: row.motivoDevolucao || null,
    homologadoEm: row.homologadoEm || null,
    criterios: row.criterios || null,
    pendencias: row.pendencias || null,
    embarcadores: embarcadores.length ? embarcadores : row.embarcador ? [{ id: row.embarcador.id, nome: row.embarcador.nome }] : [],
  };
}

export function lerResposta(r) {
  const items = (r && (r.items || r.itens)) || [];
  const count = r && (r.count !== undefined ? r.count : r.total);
  return { items: items.map(normalizarLinha), count: Number(count) || items.length };
}

const CAMPO_LABEL = { cnpj: "CNPJ", razaoSocial: "razão social", cep: "CEP", logradouro: "endereço (logradouro)" };

// O que falta, escrito (sem depender do mouse). Vazio quando os critérios fecham.
export function textoPendencias(criterios) {
  if (!criterios) return [];
  const dp = criterios.dadosPrincipais || {};
  const ti = criterios.contatoTi || {};
  const out = [];
  const faltam = dp.faltando || dp.pendencias || [];
  if (dp.ok === false) out.push(`Falta: ${faltam.map((c) => CAMPO_LABEL[c] || c).join(", ") || "dados principais"}`);
  if (ti.ok === false) out.push("Sem contato de TI");
  return out;
}

const linkStyle = { background: "none", border: 0, padding: 0, color: AUTOPILOT_COLORS.title, fontWeight: 600, cursor: "pointer", textAlign: "left" };
const acaoStyle = { fontSize: "0.72rem", padding: "0.15rem 0.5rem", marginRight: 4, marginTop: 4 };
const seloBotaoStyle = { background: "none", border: 0, padding: 0, cursor: "pointer", display: "inline-block", marginRight: 4, marginBottom: 4 };

// Selo do critério: clicável, abre o cadastro na aba que resolve (PO1-3492).
function CriterioBadge({ ok, label, detalhe, onAbrir }) {
  const selo = <Badge tipo={ok ? "done" : "pend"}>{label}</Badge>;
  if (!onAbrir) {
    return <span title={detalhe || ""} style={{ display: "inline-block", marginRight: 4, marginBottom: 4 }}>{selo}</span>;
  }
  return (
    <button type="button" title={`${detalhe || ""} · clique para abrir`} style={seloBotaoStyle} onClick={onAbrir}>
      {selo}
    </button>
  );
}

// Só Dados e Contato TI (PO1-3281): o canal de CT-e e o 1º CT-e saíram dos critérios.
// Contato TI não bloqueia a aprovação (PO1-3498), só avisa.
function Criterios({ criterios, onAbrir }) {
  if (!criterios) return <span style={mutedStyle}>—</span>;
  const dp = criterios.dadosPrincipais || {};
  const ti = criterios.contatoTi || {};
  const faltam = dp.faltando || dp.pendencias || [];
  const pendTi = ti.pendencias || [];
  return (
    <div>
      <CriterioBadge
        ok={dp.ok}
        label="Dados"
        detalhe={dp.ok ? "Dados principais completos" : `Faltam: ${faltam.map((c) => CAMPO_LABEL[c] || c).join(", ")}`}
        onAbrir={onAbrir ? () => onAbrir("dados") : null}
      />
      <CriterioBadge
        ok={ti.ok}
        label="Contato TI"
        detalhe={ti.contato ? `${ti.contato.nome} <${ti.contato.email}>` : `${pendTi.length ? pendTi.join("; ") : "Sem contato de TI"} (não bloqueia a aprovação)`}
        onAbrir={onAbrir ? () => onAbrir("contatos") : null}
      />
    </div>
  );
}

export default function HomologacaoTransportadoras() {
  const { addToast } = useToast();
  const [status, setStatus] = useState("pendente");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [devolver, setDevolver] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [agindo, setAgindo] = useState(null);
  const [cadastro, setCadastro] = useState(null); // { idTransportadora, idEmbarcador, aba }

  // silencioso (PO1-3497): recarrega em segundo plano, sem trocar a tabela pelo
  // "Carregando…" (ao fechar o cadastro e depois das ações da linha).
  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setLoading(true);
    setErro(null);
    try {
      const r = await listarHomologacao({ status, q: q || undefined, page, pageSize });
      setData(lerResposta(r));
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || err.message;
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "Erro ao listar a homologação", description: msg });
    } finally {
      setLoading(false);
    }
  }, [status, q, page, pageSize, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aprovar = async (row) => {
    setAgindo(row.key);
    try {
      const out = await aprovarTransportadoraHomologacao(row.idTransportadora);
      const n = Array.isArray(out.liberados) ? out.liberados.length : Number(out.liberados) || row.embarcadores.length;
      addToast({
        place: "tr",
        type: "success",
        title: "Transportadora homologada",
        description: `${row.fantasia || row.razaoSocial}: ${n} embarcador(es) liberado(s) para o onboarding (pastas, usuário SFTP e portal).`,
      });
      carregar({ silencioso: true });
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      addToast({
        place: "tr",
        type: "danger",
        title: "Não foi possível aprovar",
        description: d.pendencias ? d.pendencias.join("; ") : d.error || err.message,
      });
      if (d.pendencias) carregar({ silencioso: true });
    } finally {
      setAgindo(null);
    }
  };

  const reenviar = async (row) => {
    setAgindo(row.key);
    try {
      await reenviarTransportadoraHomologacao(row.idTransportadora);
      addToast({ place: "tr", type: "success", title: "Reenviada para homologação", description: `${row.fantasia || row.razaoSocial} voltou para a fila.` });
      carregar({ silencioso: true });
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      addToast({ place: "tr", type: "danger", title: "Não foi possível reenviar", description: d.pendencias ? d.pendencias.join("; ") : d.error || err.message });
    } finally {
      setAgindo(null);
    }
  };

  const abrirCadastro = (row, aba) =>
    setCadastro({ idTransportadora: row.idTransportadora, idEmbarcador: row.embarcadores[0] ? row.embarcadores[0].id : null, aba });

  const completarReceita = async (row) => {
    setAgindo(row.key);
    try {
      const out = await completarReceitaHomologacao(row.idTransportadora);
      const n = (out.alterados || []).length;
      const falta = textoPendencias(out.criterios).filter((t) => t.indexOf("Falta") === 0);
      addToast({
        place: "tr",
        type: n ? "success" : "warning",
        title: n ? "Cadastro completado pela Receita" : "A Receita não trouxe o que falta",
        description: n
          ? `${row.fantasia || row.razaoSocial}: ${n} campo(s) preenchido(s).${falta.length ? ` ${falta.join("; ")}.` : ""}`
          : "Abra o cadastro e preencha à mão.",
      });
      carregar({ silencioso: true });
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      addToast({ place: "tr", type: "danger", title: "Não foi possível consultar a Receita", description: d.error || err.message });
    } finally {
      setAgindo(null);
    }
  };

  const confirmarDevolver = async () => {
    if (!devolver || !motivo.trim()) return;
    setAgindo(devolver.key);
    try {
      await devolverTransportadoraHomologacao(devolver.idTransportadora, { motivo: motivo.trim() });
      addToast({ place: "tr", type: "success", title: "Transportadora devolvida", description: "Quem deu a entrada vê o motivo e reenvia depois de corrigir; a transportadora foi avisada por e-mail." });
      setDevolver(null);
      setMotivo("");
      carregar({ silencioso: true });
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      addToast({ place: "tr", type: "danger", title: "Não foi possível devolver", description: d.error || err.message });
    } finally {
      setAgindo(null);
    }
  };

  const filtrosBotoes = (
    <ButtonGroup size="sm" aria-label="Filtro de status">
      {STATUS_OPTIONS.map((opt) => (
        <Button key={opt.value} color="info" outline={opt.value !== status} onClick={() => { setStatus(opt.value); setPage(1); }} aria-pressed={opt.value === status}>
          {opt.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  return (
    <div className="content">
      <PageHero
        eyebrow="Jornada da transportadora"
        title="Homologação de Transportadoras"
        description="Uma transportadora por linha, com os embarcadores que esperam por ela. Aprovar exige dados principais completos; o contato de TI é recomendado, mas não bloqueia. O canal de CT-e nasce no onboarding, depois da aprovação."
        actions={<PageHeroRefreshButton onClick={() => carregar()} loading={loading} />}
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar transportadora"
        searchHint="Apelido, razão social, CNPJ ou embarcador"
        searchPlaceholder="Digite transportadora ou embarcador"
        searchValue={qInput}
        onSearchChange={setQInput}
        onSearchSubmit={(valor) => { setQ(String(valor || "").trim()); setPage(1); }}
        showFiltersButton={false}
        showActions={false}
        extra={filtrosBotoes}
      />

      <ShellCard>
        <SectionCard
          title="Transportadoras na fila"
          description="Clique no critério pendente para abrir o cadastro na aba que resolve (Dados ou Contatos). Aprovar libera todos os embarcadores listados de uma vez."
          action={<span style={{ ...mutedStyle, whiteSpace: "nowrap" }}>{data.count} resultado(s)</span>}
        >
          {loading ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
              <span>Carregando…</span>
            </div>
          ) : erro ? (
            <div style={stateBoxStyle} role="alert">Não foi possível carregar a lista: {erro}</div>
          ) : data.items.length === 0 ? (
            <div style={stateBoxStyle}>Nenhuma transportadora neste filtro.</div>
          ) : (
            <>
              <Table size="sm" responsive hover className="hub-thead-accent">
                <thead className="text-primary">
                  <tr>
                    <th>Transportadora</th>
                    <th>Embarcadores</th>
                    <th>Critérios</th>
                    <th>Situação</th>
                    <th>Desde</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => {
                    const pronto = !!(row.criterios && row.criterios.todosOk);
                    const homologado = STATUS_HOMOLOGADOS.indexOf(row.status) >= 0;
                    const devolvido = row.status === "DEVOLVIDO";
                    const ocupado = agindo === row.key;
                    return (
                      <tr key={row.key}>
                        <td>
                          <button
                            type="button"
                            style={linkStyle}
                            title="Abrir o cadastro da transportadora"
                            onClick={() => abrirCadastro(row, "dados")}
                          >
                            {row.fantasia || row.razaoSocial || "—"}
                          </button>
                          {row.fantasia && row.razaoSocial && row.fantasia !== row.razaoSocial ? <div style={mutedStyle}>{row.razaoSocial}</div> : null}
                          <div style={mutedStyle}>{formatDocument(row.cnpj)}</div>
                        </td>
                        <td>
                          {row.embarcadores.length ? (
                            row.embarcadores.map((e) => (
                              <span key={`${row.key}-${e.id}`} style={chipStyle} title={e.criadoEm ? `Desde ${formatDateTime(e.criadoEm)}` : ""}>
                                {e.nome || `Embarcador ${e.id}`}
                              </span>
                            ))
                          ) : (
                            <span style={mutedStyle}>—</span>
                          )}
                        </td>
                        <td>
                          <Criterios criterios={row.criterios} onAbrir={homologado ? null : (aba) => abrirCadastro(row, aba)} />
                          {!homologado && row.criterios && row.criterios.dadosPrincipais && row.criterios.dadosPrincipais.ok === false && (
                            <Button size="sm" color="info" outline style={acaoStyle} disabled={ocupado} onClick={() => completarReceita(row)} title="Preenche só o que está vazio, com os dados da Receita">
                              Completar pela Receita
                            </Button>
                          )}
                        </td>
                        <td>
                          <Badge tipo={homologado ? "done" : devolvido ? "pend" : pronto ? "auto" : "lock"}>
                            {devolvido ? "Devolvida" : FASE_LABEL[row.fase] || FASE_LABEL[row.status] || row.fase || row.status}
                          </Badge>
                          {devolvido && row.motivoDevolucao && <div style={mutedStyle}>{row.motivoDevolucao}</div>}
                          {homologado && row.homologadoEm && <div style={mutedStyle}>{formatDateTime(row.homologadoEm)}</div>}
                          {!pronto && !homologado && row.pendencias && row.pendencias.length ? (
                            <div style={mutedStyle}>{row.pendencias.join("; ")}</div>
                          ) : null}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{row.desde ? formatDateTime(row.desde) : "—"}</td>
                        <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                          {!homologado && (
                            <>
                              {devolvido ? (
                                <Button size="sm" color="info" outline disabled={ocupado} title="Voltar para a fila depois de corrigir o cadastro" onClick={() => reenviar(row)}>
                                  {ocupado ? <Spinner size="sm" /> : "Reenviar"}
                                </Button>
                              ) : (
                                <>
                                  <Button size="sm" color="success" outline disabled={!pronto || ocupado} title={pronto ? "" : "Critérios pendentes"} onClick={() => aprovar(row)}>
                                    {ocupado ? <Spinner size="sm" /> : "Aprovar"}
                                  </Button>{" "}
                                  <Button size="sm" color="warning" outline disabled={ocupado} onClick={() => { setDevolver(row); setMotivo(""); }}>
                                    Devolver
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <TablePagination page={page} pageSize={pageSize} total={data.count} pageSizes={PAGE_SIZES} itemLabel="transportadora(s)" onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />
            </>
          )}
        </SectionCard>
      </ShellCard>

      {cadastro && (
        <TransportadoraDetalheModal
          transportadoraId={cadastro.idTransportadora}
          embarcadorId={cadastro.idEmbarcador}
          abaInicial={cadastro.aba}
          onClose={() => { setCadastro(null); carregar({ silencioso: true }); }}
        />
      )}

      <Modal isOpen={!!devolver} toggle={agindo ? undefined : () => setDevolver(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>Devolver a transportadora</div>
          <div style={{ ...mutedStyle, marginBottom: "1rem" }}>
            {devolver && `${devolver.fantasia || devolver.razaoSocial} · ${devolver.embarcadores.map((e) => e.nome).filter(Boolean).join(", ")}. `}
            Vale para todos os embarcadores que esperam por ela. Quem deu a entrada vê o motivo, corrige e reenvia para reavaliação.
          </div>
          <FormGroup>
            <Label style={{ color: AUTOPILOT_COLORS.text }}>Motivo * (até 500 caracteres)</Label>
            <Input type="textarea" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 500))} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDevolver(null)} disabled={!!agindo}>Cancelar</Button>
          <Button color="warning" onClick={confirmarDevolver} disabled={!!agindo || !motivo.trim()}>{agindo ? <Spinner size="sm" /> : "Devolver"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
